/**
 * Build booking options from course data
 * Shared logic for both widget and widgetsv4 apps
 */

import type { BookingOptionProps } from "../../components/checkout/booking-option";
import { deriveV2SessionPrice } from "../pricing";
import type {
  CourseData,
  BundleData,
  TicketTypeData,
  BookingOptionType,
} from "./types";

// Course type constants
const CourseTypes = {
  TERMLY: "termly",
  REGULAR: "regular",
} as const;

// Course status constants
const CourseStatuses = {
  APPROVED: "approved",
} as const;

export type BookingOptionWithType = BookingOptionProps & {
  type: BookingOptionType;
  data?: Record<string, unknown>;
};

type BuildOptionsParams = {
  course: CourseData;
  bundles: BundleData[];
  ticketTypes: TicketTypeData[];
  ticketTypesV2?: any[];
  dismissBundles?: boolean;
  onSelect: (type: BookingOptionType, data?: Record<string, unknown>) => void;
};

function lowestV2Price(
  tickets: any[] | undefined,
  filterFn: (tt: any) => boolean,
  numberOfSessions: number,
): { price: number; isFromPrice: boolean } | undefined {
  if (!tickets || tickets.length === 0) return undefined;
  const eligible = tickets.filter(
    tt => (tt.status ?? "enabled") === "enabled" && filterFn(tt),
  );
  if (eligible.length === 0) return undefined;
  const prices = eligible
    .map(tt => deriveV2SessionPrice(tt, numberOfSessions))
    .sort((a, b) => a - b);
  const unique = Array.from(new Set(prices));
  const lowest = unique[0];
  if (lowest === undefined) return undefined;
  return { price: lowest, isFromPrice: unique.length > 1 };
}

/**
 * Build booking options from course data.
 * This replicates the logic from the widget app's BookingOptions component.
 */
export function buildBookingOptions({
  course,
  bundles,
  ticketTypes: _ticketTypes,
  ticketTypesV2,
  dismissBundles = false,
  onSelect,
}: BuildOptionsParams): BookingOptionWithType[] {
  const options: BookingOptionWithType[] = [];
  const { summary, discount } = course;

  // Check if course is approved
  if (course.status !== CourseStatuses.APPROVED) {
    return [];
  }

  // TERMLY courses
  if (course.type === CourseTypes.TERMLY) {
    // Unified Term option (replaces separate Full Term and Pro-rata Term options)
    const hasTermDates = summary.hasFullTermDates || summary.hasProRataTermDates;
    if (hasTermDates) {
      // Determine session count based on what's available
      const numberOfSessions = summary.hasFullTermDates
        ? (summary.numberOfTermlySessions ?? 0)
        : (summary.numberOfAdditionalProRataDates ?? 0) + 1;

      // Manual mode: the partner-entered price is the whole-course price —
      // display it as-is, no session multiplication.
      // Auto + v2: compute from v2 tickets using shape and cadence.
      // Auto + v1: server-computed summary prices already apply the right
      // typeCharge logic.
      const summaryFallbackPrice = summary.hasFullTermDates
        ? summary.fullTermPrice
        : summary.proRataPrice?.price;
      const summaryFallbackIsFromPrice = summary.hasFullTermDates
        ? ((summary.additionalFullTermPrices?.length ?? 0) > 0 || summary.hasProRataTermDates)
        : true;

      let price: number | undefined;
      let isFromPrice = false;
      if (course.priceDisplayMode === "manual") {
        price = course.price ?? summaryFallbackPrice;
        isFromPrice = course.isPriceFrom === true;
      } else if (course.ticketVersion === "v2") {
        const v2Result = lowestV2Price(
          ticketTypesV2,
          tt => tt.bookingType !== "trial" && tt.bookingType !== "drop-in",
          numberOfSessions,
        );
        if (v2Result) {
          price = v2Result.price;
          isFromPrice = v2Result.isFromPrice;
        } else {
          price = summaryFallbackPrice;
          isFromPrice = summaryFallbackIsFromPrice;
        }
      } else {
        price = summaryFallbackPrice;
        isFromPrice = summaryFallbackIsFromPrice;
      }

      // Session counts that match the "Choose a term" step. Fall back to the
      // legacy figure when the API hasn't populated the new fields yet — but
      // never print the misleading word "remaining".
      const bookableTermsCount = summary.bookableTermsCount ?? 0;
      const bookableSessions =
        summary.primaryTermBookableSessions ?? numberOfSessions;
      const totalSessions =
        summary.primaryTermTotalSessions ?? bookableSessions;
      const isProRataTerm = summary.primaryTermIsProRata ?? false;

      const sessionsText =
        isProRataTerm && totalSessions > bookableSessions
          ? `${bookableSessions} of ${totalSessions} sessions`
          : bookableSessions === 1
            ? "1 session"
            : `${bookableSessions} sessions`;
      const proRataSuffix = isProRataTerm ? " · pro rata" : "";

      const multipleTerms =
        bookableTermsCount > 1 || summary.hasMultipleBookableTerms === true;
      const description = multipleTerms
        ? bookableTermsCount > 1
          ? `${bookableTermsCount} terms available`
          : "Multiple terms available"
        : summary.termName
          ? `${summary.termName} · ${sessionsText}${proRataSuffix}`
          : `${sessionsText}${proRataSuffix}`;

      if (typeof price === "number") {
        options.push({
          type: "term",
          title: "Term",
          description,
          price,
          isFromPrice,
          discount,
          onSelect: () => onSelect("term"),
        });
      }
    }

    // Trial option — for v2 trial tickets apply shape (e.g. PWYW →
    // suggestedPrice). Trial is always a single session so cadence
    // multiplier doesn't apply.
    if (summary.hasAvailableTermTrialDates) {
      let trialPrice = summary.trialPrice;
      const trialV2 = ticketTypesV2?.find(
        tt => tt.id === course.trialTicketTypeId,
      );
      if (trialV2) {
        trialPrice = deriveV2SessionPrice(trialV2, 1);
      }
      options.push({
        type: "trial",
        title: "Trial this event",
        description: "Book a trial session to get a taster of the full term",
        price: trialPrice,
        discount,
        onSelect: () => onSelect("trial"),
      });
    }

    // Drop-in option — same v2 shape handling as trial.
    if (summary.hasAvailableTermDropInDates) {
      let dropInPrice = summary.dropInPrice;
      const dropInV2 = ticketTypesV2?.find(
        tt => tt.id === course.dropInTicketTypeId,
      );
      if (dropInV2) {
        dropInPrice = deriveV2SessionPrice(dropInV2, 1);
      }
      options.push({
        type: "dropIn",
        title: "Drop-in",
        description: "1 Session",
        price: dropInPrice,
        discount,
        onSelect: () => onSelect("dropIn"),
      });
    }
  } else if (summary.hasDates) {
    // REGULAR courses
    const numberOfDates = summary.nextAvailableAdditionalDates?.length ?? 0;
    const numberOfSessions = numberOfDates + 1;
    const description =
      numberOfSessions === 1 ? "1 event" : `${numberOfSessions} events`;

    // Same three-branch pattern as TERMLY: manual / auto-v2 / auto-v1.
    let displayPrice: number;
    let isFromPrice: boolean;
    if (course.priceDisplayMode === "manual") {
      displayPrice = course.price ?? summary.price;
      isFromPrice = course.isPriceFrom === true;
    } else if (course.ticketVersion === "v2") {
      const v2Result = lowestV2Price(
        ticketTypesV2,
        tt => tt.bookingType !== "trial" && tt.bookingType !== "drop-in",
        numberOfSessions,
      );
      if (v2Result) {
        displayPrice = v2Result.price;
        isFromPrice = v2Result.isFromPrice;
      } else {
        displayPrice = summary.price;
        isFromPrice = (summary.additionalPrices?.length ?? 0) > 0;
      }
    } else {
      displayPrice = summary.price;
      isFromPrice = (summary.additionalPrices?.length ?? 0) > 0;
    }

    // Format duration for title. Event listings are a single date, so the
    // option is about tickets, not about picking a session length.
    const isEventListing = course.listingType === "event";
    let title = isEventListing ? "Get tickets" : "Book event";
    if (!isEventListing && course.duration) {
      const hours = Math.floor(course.duration / 60);
      const minutes = course.duration % 60;
      if (hours > 0 && minutes > 0) {
        title = `${hours}h ${minutes}m session`;
      } else if (hours > 0) {
        title = hours === 1 ? "1 hour session" : `${hours} hour session`;
      } else {
        title = `${minutes} minute session`;
      }
    }

    options.push({
      type: "regular",
      title,
      description,
      price: displayPrice,
      isFromPrice,
      discount,
      onSelect: () => onSelect("regular"),
    });
  }

  // Group Schedule (Flextime) option
  if (course.isGroupScheduleEnabled && course.groupScheduleBriefing) {
    const { minNumberOfGuests, maxNumberOfGuests, price } =
      course.groupScheduleBriefing;
    const description =
      minNumberOfGuests === maxNumberOfGuests
        ? `Minimum ${minNumberOfGuests} guests to book`
        : `For groups of ${minNumberOfGuests} to ${maxNumberOfGuests} guests`;

    options.push({
      type: "groupSchedule",
      title: "Private Booking",
      description,
      price,
      discount,
      onSelect: () => onSelect("groupSchedule"),
    });
  }

  // Appointment option
  if (
    course.isAppointmentScheduleEnabled &&
    course.appointmentScheduleBriefing
  ) {
    const { price, additionalPrices } = course.appointmentScheduleBriefing;

    options.push({
      type: "appointment",
      title: "By Appointment",
      description: "Book anytime you want",
      price,
      isFromPrice: (additionalPrices?.length ?? 0) > 0,
      discount,
      onSelect: () => onSelect("appointment"),
    });
  }

  // Rental option — present when any v2 ticket is a configured rental
  // (bookingType "rental" with a fraction size). Price shown is the lowest
  // price-per-fraction across rental tickets ("From £X / fraction").
  const rentalTickets = (ticketTypesV2 ?? []).filter(
    (tt: any) =>
      (tt.bookingType === "rental" || course.listingType === "rental") &&
      (tt.rentalFractionMinutes ?? 0) > 0,
  );
  if (rentalTickets.length > 0) {
    const lowest = Math.min(...rentalTickets.map((tt: any) => tt.price ?? 0));
    // The ticket's own name, not a generic "Rent". A listing that hires out one
    // thing should say what it is ("Electric bike"), and the partner already
    // named it. Only meaningful when there IS one name to show: with several
    // rental tickets the option is a "from £X" roll-up over all of them, so a
    // single ticket's name would misdescribe what the customer is picking.
    const soleRentalName =
      rentalTickets.length === 1 ? rentalTickets[0]?.name?.trim() : "";
    options.push({
      type: "rental",
      title: soleRentalName || "Rent",
      description: "Pick your dates and times",
      price: lowest,
      isFromPrice: rentalTickets.length > 1,
      discount,
      onSelect: () => onSelect("rental"),
    });
  }

  // Bundle options
  if (!dismissBundles) {
    const currencySymbol = getCurrencySymbol(course.currency);

    for (const bundle of bundles) {
      if (bundle.status !== "enabled") continue;

      // Check expiration
      if (bundle.typeOfExpiration === "timeRange" && bundle.expirationEndDate) {
        const hasExpired = new Date(bundle.expirationEndDate) < new Date();
        if (hasExpired) continue;
      }

      // Build description
      let description = bundle.description || "";
      if (!description) {
        const savings = summary.price * bundle.totalSessions - bundle.price;
        if (savings > 0) {
          description = `Save ${currencySymbol}${(savings / 100).toFixed(0)}`;
        }
      }
      if (bundle.typeOfExpiration === "timeRange") {
        const start = new Date(bundle.expirationStartDate!).toLocaleDateString(
          "en-GB",
          { day: "numeric", month: "long" },
        );
        const end = new Date(bundle.expirationEndDate!).toLocaleDateString(
          "en-GB",
          { day: "numeric", month: "long" },
        );
        description = `${start} - ${end}`;
      }

      options.push({
        type: "bundle",
        title: bundle.name,
        description,
        price: bundle.price,
        discount,
        onSelect: () =>
          onSelect("bundle", {
            bundle,
            usingBundlesV2: bundle.usingBundlesV2,
          }),
        data: { bundleId: bundle.id },
      });
    }
  }

  // A rental listing sells access to a resource pool, so only the rental option
  // is meaningful on it. Anything else — the "Standard" ticket auto-created at
  // listing creation, an appointment ticket, a bundle — would take the
  // customer's money and consume zero pool units, leaving the partner
  // overbooked with no record of it. Filtering here rather than gating each
  // options.push above keeps the rule in one place.
  //
  // Presentation only: the server rejects non-rental items on a rental listing
  // (ValidateBasketItems), which is what actually protects against a stale page
  // or a hand-made basket payload.
  if (course.listingType === "rental") {
    return options.filter((option) => option.type === "rental");
  }

  return options;
}

function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    AUD: "A$",
    CAD: "C$",
  };
  return symbols[currency] || currency;
}
